import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaVideo } from 'react-icons/fa';
import { graphqlRequest } from '../utils/graphqlRequest';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';

const STATUS = `query { googleCalendarConnection }`;
const CONNECT = `mutation { googleCalendarConnectUrl }`;
const DISCONNECT = `mutation { disconnectGoogleCalendar }`;

const RESULT_MESSAGES = {
  connected: ['success', 'Google account connected. New virtual meetings will get a Meet link.'],
  cancelled: ['info', 'Google sign-in was cancelled.'],
  failed: ['error', 'Could not connect Google. Please try again.'],
};

/** Settings → Video Meetings: each business connects its own Google account to host Meet links. */
export default function VideoMeetingSettings({ SettingsCard }) {
  const { get, patch } = useAdminPageSearch();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () =>
    graphqlRequest(STATUS)
      .then((res) => setEmail(res?.googleCalendarConnection || null))
      .catch(() => setEmail(null))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // Result of the Google sign-in redirect (?google=connected|cancelled|failed).
    const result = RESULT_MESSAGES[get('google')];
    if (result) {
      toast[result[0]](result[1]);
      patch({}, ['google']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      setBusy(true);
      const res = await graphqlRequest(CONNECT);
      window.location.href = res.googleCalendarConnectUrl;
    } catch (err) {
      toast.error(err?.message || 'Could not start Google sign-in.');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google? New virtual meetings will not get a Meet link.')) return;
    try {
      setBusy(true);
      await graphqlRequest(DISCONNECT);
      setEmail(null);
      toast.success('Google account disconnected.');
    } catch (err) {
      toast.error(err?.message || 'Could not disconnect Google.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="st-layout">
      <SettingsCard title="Google Meet" icon={<FaVideo />}>
        <div className="st-help-text" style={{ marginBottom: 16 }}>
          Connect your business&apos;s Google account. Virtual meetings are then created on your
          Google Calendar with a Meet link, which is sent in the invite email. To start a meeting
          as host, open it while signed in to this Google account.
        </div>
        {loading ? (
          <div className="st-help-text">Checking connection…</div>
        ) : email ? (
          <div className="st-pref-row">
            <div className="st-pref-text">
              <div className="st-pref-title">Connected</div>
              <div className="st-pref-sub">{email}</div>
            </div>
            <button type="button" className="acct-btn-secondary" onClick={disconnect} disabled={busy}>
              {busy ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <div className="st-form-actions">
            <button type="button" className="acct-btn-primary" onClick={connect} disabled={busy}>
              {busy ? 'Opening Google…' : 'Connect Google account'}
            </button>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
